/**
 * BKT Birthday Campaign — free gift auto-add script.
 *
 * Watches the cart subtotal (excluding the gift product itself) and keeps a
 * line item of "BKT Hydra Mask 300g" in the cart matching the spend tier the
 * customer currently qualifies for. The actual FREE price comes from the
 * native "Buy X, Get Y" automatic discounts set up in Shopify Admin > Discounts
 * (titled "BKT Birthday Gift — 1/2/3 Free Hydra Mask(s)") — this script only
 * adds/removes the item to the cart, it never sets or changes its price.
 *
 * Self-healing: if two updates land close together (e.g. a customer quickly
 * adding items to climb from one spend tier to the next), the cart can briefly
 * end up with two separate gift lines instead of one. Every sync now checks
 * for that and merges duplicates back into a single line before deciding the
 * correct quantity, so it can't leave a stray full-price line behind.
 *
 * Safe to remove: delete this <script> include from theme.liquid (search for
 * "BKT Birthday Campaign" in theme.liquid) or just delete this file — nothing
 * else in the theme depends on it. It also stops itself automatically after
 * the campaign end date (see the liquid gate in theme.liquid), so no manual
 * cleanup is required once the promo period passes.
 */
(function () {
  var CONFIG = window.BKT_BIRTHDAY_CAMPAIGN;
  if (!CONFIG || !CONFIG.enabled) return;

  var GIFT_VARIANT_ID = CONFIG.giftVariantId;
  var PROP_KEY = CONFIG.propKey;
  var PROP_VALUE = CONFIG.propValue;
  var EXCLUDED = CONFIG.customerExcluded === true;
  // Highest threshold first so we always match the best tier the cart qualifies for.
  var TIERS = (CONFIG.tiers || []).slice().sort(function (a, b) {
    return b.min - a.min;
  });

  var syncing = false;
  var pendingResync = false;

  function isGiftVariantLine(item) {
    return item.variant_id === GIFT_VARIANT_ID;
  }

  function isOurGiftLine(item) {
    return isGiftVariantLine(item) && item.properties && item.properties[PROP_KEY] === PROP_VALUE;
  }

  // Mirrors the eligibility rule used by the Shopify discounts: the gift
  // product itself (any unit of it, free or paid) never counts toward the
  // spend threshold.
  function eligibleSubtotalCents(cart) {
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!isGiftVariantLine(item)) {
        total += item.line_price;
      }
    }
    return total;
  }

  function desiredQty(subtotalCents) {
    if (EXCLUDED) return 0;
    var subtotal = subtotalCents / 100;
    for (var i = 0; i < TIERS.length; i++) {
      if (subtotal >= TIERS[i].min) return TIERS[i].qty;
    }
    return 0;
  }

  // Returns ALL of our gift lines (normally 0 or 1; can briefly be more than
  // one if two updates raced each other).
  function findOurGiftLines(cart) {
    var lines = [];
    for (var i = 0; i < cart.items.length; i++) {
      if (isOurGiftLine(cart.items[i])) {
        lines.push({ item: cart.items[i], line: i + 1 });
      }
    }
    return lines;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  function sync() {
    if (syncing) {
      pendingResync = true;
      return;
    }
    syncing = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var ourLines = findOurGiftLines(cart);

        // Self-heal: collapse any duplicate gift lines into the first one
        // before deciding anything. Remove extras from the highest line
        // number down so earlier positions stay valid mid-loop.
        if (ourLines.length > 1) {
          var toRemove = ourLines.slice(1).sort(function (a, b) {
            return b.line - a.line;
          });
          var chain = Promise.resolve();
          toRemove.forEach(function (entry) {
            chain = chain.then(function () {
              return postJSON('/cart/change.js', { line: entry.line, quantity: 0 });
            });
          });
          return chain.then(function () {
            pendingResync = true;
          });
        }

        var want = desiredQty(eligibleSubtotalCents(cart));
        var existing = ourLines[0] || null;
        var have = existing ? existing.item.quantity : 0;

        if (want === have) return null;

        if (want === 0 && existing) {
          return postJSON('/cart/change.js', { line: existing.line, quantity: 0 });
        }

        if (want > 0 && !existing) {
          var props = {};
          props[PROP_KEY] = PROP_VALUE;
          return postJSON('/cart/add.js', {
            items: [{ id: GIFT_VARIANT_ID, quantity: want, properties: props }],
          });
        }

        if (want > 0 && existing) {
          return postJSON('/cart/change.js', { line: existing.line, quantity: want });
        }
      })
      .catch(function (err) {
        // Fail silently for the shopper; log for diagnostics.
        console.error('[BKT Birthday Campaign] gift sync failed:', err);
      })
      .then(function () {
        syncing = false;
        if (pendingResync) {
          pendingResync = false;
          sync();
        }
      });
  }

  // Run once the page settles.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  // Catch AJAX cart changes regardless of which theme component triggered them
  // (add-to-cart buttons, cart drawer qty steppers, etc.) by watching fetch().
  var originalFetch = window.fetch;
  window.fetch = function () {
    var result = originalFetch.apply(this, arguments);
    try {
      var target = arguments[0];
      var url = typeof target === 'string' ? target : target && target.url;
      if (url && url.indexOf('/cart/') !== -1) {
        result.then(function () {
          setTimeout(sync, 300);
        });
      }
    } catch (e) {
      /* noop */
    }
    return result;
  };

  // Belt-and-braces poll in case a cart change happens outside fetch()
  // (e.g. a full-page form post back to /cart).
  setInterval(sync, 5000);
})();